// Basket Hub using global SignalR (compatible with both webpack and CDN)
console.log('[BasketHub] Script loaded, creating connection...');

// Use a more defensive approach for element detection
function getElement(id) {
  return document.getElementById(id);
}

const statusBadge = getElement('connection-status-indicator');

// Check if SignalR is available globally (from CDN)
if (typeof signalR === 'undefined') {
  console.error('[BasketHub] SignalR not found globally. Falling back to imported version...');
  // If CDN not available, we'll import it
  import('@microsoft/signalr').then(({ HubConnectionBuilder, LogLevel, JsonHubProtocol }) => {
    createBasketHub(HubConnectionBuilder, LogLevel, JsonHubProtocol);
  }).catch(err => {
    console.error('[BasketHub] Failed to import SignalR:', err);
  });
} else {
  // Use global SignalR from CDN
  console.log('[BasketHub] Using global SignalR from CDN');
  createBasketHub(signalR.HubConnectionBuilder, signalR.LogLevel, signalR.JsonHubProtocol);
}

function createBasketHub(HubConnectionBuilder, LogLevel, JsonHubProtocol) {
  console.log('[BasketHub] Creating connection with:', HubConnectionBuilder ? 'HubConnectionBuilder' : 'No HubConnectionBuilder');

  // Create connection
  const connection = new HubConnectionBuilder()
    .withUrl('/baskethub')
    .withHubProtocol(JsonHubProtocol ? new JsonHubProtocol() : undefined)
    .configureLogging(LogLevel ? LogLevel.Information : undefined)
    .withAutomaticReconnect()
    .build();

  async function startConnection() {
    console.log('[BasketHub] Starting connection...');
    
    try {
      await connection.start();
      console.log('[BasketHub] ? Connected:', connection.connectionId);
      updateStatus('Connected');
      
      // Test the connection with debug info
      try {
        const info = await connection.invoke('DebugInfo');
        console.log('[BasketHub] Debug info received:', info);
      } catch (err) {
        console.warn('[BasketHub] DebugInfo failed:', err.message);
      }
      
      // Get initial basket count
      try {
        await connection.invoke('SendBasketCount');
        console.log('[BasketHub] Basket count requested');
      } catch (err) {
        console.warn('[BasketHub] SendBasketCount failed:', err.message);
      }
      
    } catch (error) {
      console.error('[BasketHub] ? Connection failed:', error.message);
      updateStatus('Disconnected');
      
      // Retry in 5 seconds
      setTimeout(startConnection, 5000);
    }
  }

  function updateStatus(status) {
    console.log('[BasketHub] Status:', status);
    
    // Update mobile status indicator
    const mobileStatusBadge = getElement('connection-status-indicator');
    if (mobileStatusBadge) {
      updateStatusIndicator(mobileStatusBadge, status);
    }
    
    // Update desktop status indicator
    const desktopStatusBadge = getElement('connection-status-indicator-desktop');
    if (desktopStatusBadge) {
      updateStatusIndicator(desktopStatusBadge, status);
    }
    
    // Fallback for any status indicators
    const allStatusIndicators = document.querySelectorAll('.status-indicator');
    allStatusIndicators.forEach(indicator => {
      updateStatusIndicator(indicator, status);
    });
  }
  
  function updateStatusIndicator(element, status) {
    if (!element) return;
    
    // Remove all status classes
    element.classList.remove('status-connected', 'status-reconnecting', 'status-disconnected');
    
    // Add appropriate status class
    switch (status) {
      case 'Connected':
        element.classList.add('status-connected');
        element.title = 'Connected to server';
        break;
      case 'Reconnecting':
        element.classList.add('status-reconnecting');
        element.title = 'Reconnecting...';
        break;
      case 'Disconnected':
        element.classList.add('status-disconnected');
        element.title = 'Disconnected';
        break;
    }
  }

  // Set up connection event handlers
  connection.on('ReceiveBasketCount', function(count) {
    console.log('[BasketHub] Basket count received:', count);
    
    // Update main basketCount element (in LoginPartial)
    const basketElement = getElement('basketCount');
    if (basketElement) {
      console.log('[BasketHub] Found basketCount element:', {
        tagName: basketElement.tagName,
        className: basketElement.className,
        currentContent: basketElement.textContent,
        currentDataCount: basketElement.getAttribute('data-count'),
        parentElement: basketElement.parentElement?.tagName
      });
      
      updateBasketCount(basketElement, count);
    }
    
    // Update basket count text specifically
    const basketCountText = document.querySelector('.basket-count-text');
    if (basketCountText) {
      basketCountText.textContent = count;
      console.log('[BasketHub] Updated basket-count-text');
    }
    
    // Update basket count badge visibility
    const basketCountBadge = document.querySelector('.basket-count-badge');
    if (basketCountBadge) {
      if (count > 0) {
        basketCountBadge.style.display = 'flex';
      } else {
        basketCountBadge.style.display = 'none';
      }
      console.log('[BasketHub] Updated basket count badge visibility');
    }
    
    // Update any additional basket count elements
    const allBasketElements = document.querySelectorAll('[data-count]');
    allBasketElements.forEach((element, index) => {
      if (element !== basketElement) {
        updateBasketCount(element, count);
        console.log(`[BasketHub] Updated additional basket element ${index + 1}`);
      }
    });
    
    // Update aria-labels for all basket links
    const basketLinks = document.querySelectorAll('a[href*="basket"], a[href*="Basket"]');
    basketLinks.forEach(link => {
      link.setAttribute('aria-label', `View basket (${count} item${count !== 1 ? 's' : ''})`);
    });
    
    // If no main basket element found, log alternatives for debugging
    if (!basketElement) {
      console.warn('[BasketHub] Main basketCount element not found - checking for alternatives');
      
      const alternatives = [
        document.querySelector('[data-count]'),
        document.querySelector('.basket-count-text'),
        document.querySelector('.basket-count-badge'),
        document.querySelector('.basket-icon-container'),
        document.querySelector('.fa-shopping-cart')
      ];
      
      alternatives.forEach((alt, index) => {
        if (alt) {
          console.log(`[BasketHub] Alternative ${index + 1} found:`, alt.tagName, alt.className);
          if (index === 0 && alt.hasAttribute('data-count')) {
            // Use first data-count element as fallback
            updateBasketCount(alt, count);
            console.log('[BasketHub] Used alternative element as fallback');
          }
        }
      });
    }
    
    console.log('[BasketHub] Successfully updated all basket counts to:', count);
  });
  
  // Helper function to update basket count for different element types
  function updateBasketCount(element, count) {
    if (!element) return;
    
    // Update data attribute
    element.setAttribute('data-count', count);
    
    // Handle the new basket icon container structure
    if (element.classList.contains('basket-icon-container')) {
      console.log('[BasketHub] Detected basket-icon-container structure');
      
      const textElement = element.querySelector('.basket-count-text');
      const badgeElement = element.querySelector('.basket-count-badge');
      
      if (textElement) {
        textElement.textContent = count;
      }
      
      if (badgeElement) {
        if (count > 0) {
          badgeElement.style.display = 'flex';
        } else {
          badgeElement.style.display = 'none';
        }
      }
      
    } else if (element.className.includes('fa-stack')) {
      console.log('[BasketHub] Detected FontAwesome fa-stack structure (legacy)');
      
      // Look for existing fa-stack-text element or create one
      let textElement = element.querySelector('.fa-stack-text');
      if (!textElement) {
        textElement = document.createElement('span');
        textElement.className = 'fa-stack-text';
        element.appendChild(textElement);
        console.log('[BasketHub] Created fa-stack-text element');
      }
      
      textElement.textContent = count;
      
      // Manage badge visibility
      if (count > 0) {
        element.classList.add('has-badge');
      } else {
        element.classList.remove('has-badge');
      }
      
    } else if (element.className.includes('badge')) {
      console.log('[BasketHub] Detected Bootstrap badge structure');
      element.textContent = count;
      
      // Hide/show badge based on count
      if (count > 0) {
        element.style.display = 'flex';
      } else {
        element.style.display = 'none';
      }
      
    } else {
      console.log('[BasketHub] Using fallback update method');
      
      // Fallback: try to preserve any child elements
      const childElements = Array.from(element.children);
      element.textContent = count;
      
      // Re-append any child elements if they existed
      childElements.forEach(child => {
        if (child.tagName === 'I' || child.classList.contains('fa')) {
          element.appendChild(child);
        }
      });
    }
  }

  connection.onreconnecting(function(error) {
    console.log('[BasketHub] Reconnecting...', error?.message);
    updateStatus('Reconnecting');
  });

  connection.onreconnected(function(connectionId) {
    console.log('[BasketHub] Reconnected:', connectionId);
    updateStatus('Connected');
    
    // Refresh basket count
    connection.invoke('SendBasketCount').catch(function(err) {
      console.warn('[BasketHub] SendBasketCount after reconnect failed:', err.message);
    });
  });

  connection.onclose(function(error) {
    console.log('[BasketHub] Connection closed:', error?.message);
    updateStatus('Disconnected');
  });

  // Initialize when DOM is ready
  function initialize() {
    console.log('[BasketHub] Initializing...');
    
    // Check for required elements
    const basketEl = getElement('basketCount');
    console.log('[BasketHub] Basket element found:', !!basketEl);
    console.log('[BasketHub] Status indicator found:', !!statusBadge);
    
    // Start the connection
    startConnection();
  }

  // Start when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Export for potential external use
  window.basketConnection = connection;
  
  return connection;
}


